<template>
  <div class="floor-container" v-if="user">
    <Map
      id="floor-map"
      :class="{ 'floor-map_print': printMode }"
      :markers="computedMarkers"
      :areas="computedAreas"
      :key="compositeMapKey"
      @markerClick="handleMarkerClick"
      @markerMove="handleMarkerMove"
      @areaClick="handleMarkerClick"
      @areaChange="handleAreaChange"
      @ready="handleMapReady"
      @update:zoom="handleZoom"
    />
    <div v-if="printMode" class="print-mode-actions">
      <div class="ml-auto department-toggle markers-label-size">
        <label for="markers-label-size" class="mb-0 mr-2">Label Size</label>

        <b-form-input
          id="markers-label-size"
          class="markers-label-size__input"
          size="sm"
          type="number"
          min="5"
          max="20"
          :value="printModeTitleSize"
          @change="setPrintModeTitleSize"
        />
      </div>
      <div class="form-check ml-3 department-toggle">
        <input id="markers-hide-toggle" type="checkbox" class="form-check-input" @change="toggleMarkersHide" :checked="!markersHidden" />
        <label for="markers-hide-toggle" class="form-check-label">Display Markers</label>
      </div>
      <div class="form-check ml-3 department-toggle">
        <input
          id="department-toggle"
          type="checkbox"
          class="form-check-input"
          :checked="departmentLabelEnabled"
          @change="toggleDepartmentLabel"
        />
        <label for="department-toggle" class="form-check-label">Display Desk Numbers</label>
      </div>
      <b-btn variant="primary btn-round" class="ml-3 col-auto" @click="print" @touchend.stop.prevent="print">
        <span class="ion ion-md-print" style="font-size: 16px;"></span> Print map
      </b-btn>
      <!--<b-btn variant="default btn-round bg-white" class="ml-3 col-auto" ></b-btn>-->
      <span class="ion ion-md-close close-icon ml-3 mr-auto" @click="goToNormalMode" @touchend.stop.prevent="goToNormalMode"></span>
    </div>
    <div class="floor-actions" v-else>
      <div class="floor-actions-row">
        <MapFloorSelect style="max-width: 160px" @change="changeFloor" :active-floor="floorId" />
        <MapDrawMenu
          v-if="can(PN.POINT_CREATE) && !drawShapeId"
          @startDraw="handleStartDraw"
          :has-desk="user.booking_enabled"
          :has-area="user.area_enabled"
          :has-employee="!scimEmployeesSyncEnabled"
        />
        <b-btn
          v-if="can(PN.POINT_CREATE) && drawShapeId"
          variant="primary btn-round"
          class="ml-3 col-auto"
          @click="disableShapeDrawing"
          title="cancel map drawing"
          ref="add"
          ><span class="ion ion-md-close"></span> cancel
        </b-btn>
        <b-btn
          variant="default btn-round bg-white"
          class="ml-3 col-auto more-button"
          ref="more"
          v-click-outside="() => (this.moreEnabled = false)"
          @click="moreEnabled = !moreEnabled"
        >
          <i class="ion-ios-more"></i>
        </b-btn>
      </div>
      <div
        class="floor-timezone-banner"
        :class="{ 'floor-timezone-banner_collapsed': timezoneBannerCollapsed }"
        v-if="timezoneBannerVisible"
      >
        <button
          v-if="timezoneBannerCollapsed"
          type="button"
          class="floor-timezone-banner__icon-button floor-timezone-banner__expand"
          :title="floorTimezoneMessage"
          aria-label="Show timezone difference"
          @click="expandTimezoneBanner"
          @touchend.stop.prevent="expandTimezoneBanner"
        >
          <span class="ion ion-md-warning"></span>
        </button>
        <template v-else>
          <span class="floor-timezone-banner__text">{{ floorTimezoneMessage }}</span>
          <button
            type="button"
            class="floor-timezone-banner__icon-button floor-timezone-banner__close"
            title="Hide timezone difference"
            aria-label="Hide timezone difference"
            @click="collapseTimezoneBanner"
            @touchend.stop.prevent="collapseTimezoneBanner"
          >
            <span class="ion ion-md-close"></span>
          </button>
        </template>
      </div>
      <MapAvailabilityFilter
        class="mt-3"
        v-if="availabilityFilterEnabled"
        :floor-id="floorId"
        @change="applyAvailabilityFilter"
        @close="closeAvailabilityFilter"
      />
      <AppDropdown :dst-el="$refs.more" v-if="moreEnabled">
        <ul class="more-menu">
          <li class="dropdown-item" @click="goToLegend" @touchend.stop.prevent="goToLegend">
            <span class="ion ion-md-map action-icon"></span> Map legend
          </li>
          <li class="dropdown-item" @click="goToPrintMode" @touchend.stop.prevent="goToPrintMode">
            <span class="ion ion-md-print action-icon"></span> Print view
          </li>
          <li
            v-if="can(PN.FLOOR_UPDATE)"
            class="dropdown-item"
            @click="saveCenterZoom"
            @touchend.stop.prevent="saveCenterZoom"
            title="Save floor center and zoom as default"
          >
            <span class="ion ion-md-pin action-icon"></span> Save center and zoom
          </li>
          <li
            v-if="user.booking_enabled"
            class="dropdown-item"
            @click="availabilityFilterEnabled = true"
            @touchend.stop.prevent="availabilityFilterEnabled = true"
          >
            <span class="ion ion-md-search action-icon"></span>
            Find available desks
          </li>
          <li class="dropdown-item" @click.stop>
            <b-form-checkbox class="display-tooltips" type="checkbox" :checked="displayTooltips" @change="setDisplayTooltips($event)"
              >Display Labels</b-form-checkbox
            >
          </li>
        </ul>
      </AppDropdown>
    </div>
    <MapPointPanel v-if="!legendEnabled && activePointId" :key="activePointId" />
    <MapLegendPanel
      v-else-if="legendEnabled"
      :has-desk="user.booking_enabled"
      :has-area="user.area_enabled"
      @close="legendEnabled = false"
    />
  </div>
</template>

<script>
import '@/assets/libs/hover-dropdown/HoverDropdown'
import store, { ROOT_UPDATE, SIDENAV_CLASSES } from '@/store'
import { MAP_UPDATE } from '@/store/modules/map'
import { mapRouteSync, mapStateFromRoute, whenFloorsLoaded } from '@/components/map/mapRoute'
import { notFoundLocation, redirectLocation } from '@/router/navigation'
import { DEFAULT_POINT_LAT_LON, filterDesk, POINT_CREATE, POINT_ICONS, POINT_TYPE_MAP, POINT_UPDATE } from '@/store/modules/point'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import Id, { isLocalId } from '@/utils/Id'
import * as PN from '@/utils/PermissionNames'
import L from 'leaflet'
import AppDropdown from '@/components/shared/AppDropdown'
import { getIconOptions } from '@/components/leaflet/Icon'
import { serverCoordinateToLatLng } from '@/components/leaflet/Utils'
import MapAvailabilityFilter from '@/components/map/MapAvailabilityFilter'
import Map from '@/components/map/Map.vue'
import MapDrawMenu from '@/components/map/MapDrawMenu.vue'
import MapPointPanel from '@/components/map/MapPointPanel.vue'
import MapFloorSelect from '@/components/map/MapFloorSelect'
import MapLegendPanel from '@/components/map/MapLegendPanel.vue'
import dayjs from 'dayjs'
import { isSameTimezone, timezoneCity, timezoneUtcOffset } from '@/components/desk/utils'

function formatTooltip({ tooltip, hasSecond = true, splitNamePart = false, fontSize = undefined }) {
  if (!tooltip) return ''
  const style = fontSize ? `font-size: ${fontSize}px; line-height: ${fontSize - 2}px` : ''

  let [first, second] = tooltip.split('&nbsp;')
  hasSecond = hasSecond && second

  const firstParts = first.split(' ')
  if (splitNamePart && firstParts.length > 1) {
    first = `<span>${firstParts.slice(0, firstParts.length - 1).join(' ')}</span> <span>${firstParts[firstParts.length - 1]}</span>`
  }

  let result = `<span class="tooltip__first${!hasSecond ? ' tooltip__first_only' : ''}" style="${style}">${first}</span>`
  if (hasSecond) {
    result += `<span class="tooltip__second" style="${style}">${second}</span>`
  }

  return result
}

function getCentroid(coordinates) {
  const [xSum, ySum] = coordinates.reduce((acc, item) => {
      return [acc[0] + item[0], acc[1] + item[1]]
    }, [0, 0])
  return [xSum / coordinates.length, ySum / coordinates.length]
}

const SHAPES = {
  Marker: 'Marker',
  Polygon: 'Polygon'
}

// The timezone notice is purely informational, so it folds itself into a warning icon
// after this delay instead of covering the map for the whole session.
const TIMEZONE_BANNER_COLLAPSE_DELAY = 10 * 1000

function determineShapeById(iconId) {
  return iconId === POINT_ICONS.AREA ? SHAPES.Polygon : SHAPES.Marker
}

export default {
  name: 'MapFloor',
  // keeps the address and the map state in step; wants `routeParams` and `mapUpdate_` from here
  mixins: [mapRouteSync],
  components: {
    MapLegendPanel,
    Map,
    MapAvailabilityFilter,
    AppDropdown,
    MapDrawMenu,
    MapPointPanel,
    MapFloorSelect
  },
  data: () => ({
    PN,
    moreEnabled: false,
    drawTypeListEnabled: false,
    drawShapeId: null,
    drawShapeType: null,
    lastZoom: null,
    pointsRefreshInterval: null,
    departmentLabelEnabled: true,
    markersHidden: true,
    availabilityFilterEnabled: false,
    availabilityPointsLoaded: false,
    availabilityConditions: {},
    availabilityPoints: [],
    legendEnabled: false,
    mapCentringInProgress: false,
    printModeTitleSize: 12,
    printModeTitleSizeTimeout: 0,
    timezoneBannerCollapsed: false,
    timezoneBannerCollapseTimeout: null,
    // The Free Desks list links here with a desk and a window to book. Captured once, with
    // the point it was meant for: routeParams rebuilds the query from scratch on every
    // change, so a window left in it would be copied forward and prefill a booking on every
    // desk opened afterwards.
    linkedBooking: null
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('map', ['map', 'floorId', 'activePointId', 'printMode', 'editMode', 'displayTooltips']),
    ...mapState('settings', ['generalSettings']),
    ...mapGetters('point', ['getPoint', 'points']),
    ...mapGetters('pointIcon', ['getPointIcon']),
    ...mapGetters('floor', ['getFloor']),
    ...mapGetters('building', ['getBuilding']),
    ...mapGetters('rbac', ['can']),
    ...mapGetters('settings', ['scimEmployeesSyncEnabled']),
    compositeMapKey() {
      return `${this.floorId}--${this.displayTooltips}`
    },
    activePoint() {
      return this.activePointId ? this.getPoint(this.activePointId) : null
    },
    routeParams() {
      const params = {
        floorId: this.floorId ? this.floorId : undefined
      }

      // carried only while the desk the link named is still the open one - see linkedBooking
      const booking = this.linkedBooking?.pointId === String(this.activePointId) ? this.linkedBooking : null

      const query = {
        pointId: this.activePointId ? this.activePointId : undefined,
        print: this.printMode ? this.printMode : undefined,
        edit: this.editMode ? this.editMode : undefined,
        bookFrom: booking ? booking.bookFrom : undefined,
        bookTo: booking ? booking.bookTo : undefined
      }

      return { params, query }
    },
    computedPoints() {
      return this.points.filter((v) => {
        return (
          filterDesk(v.icon_id, this.user?.booking_enabled) &&
          (v.id === this.activePointId || !isLocalId(v.id)) &&
          v.icon_id !== POINT_ICONS.AREA
        )
      })
    },
    floor() {
      return this.getFloor(this.floorId)
    },
    floorTimezone() {
      const building = this.floor ? this.getBuilding(this.floor.building_id) : null
      return building ? building.timezone : null
    },
    floorTimezoneCity() {
      return timezoneCity(this.floorTimezone)
    },
    floorTimezoneUtcOffset() {
      return timezoneUtcOffset(this.floorTimezone)
    },
    floorTimezoneMismatch() {
      return !!this.floorTimezone && !isSameTimezone(this.floorTimezone, dayjs.tz.guess())
    },
    localTimezoneUtcOffset() {
      return timezoneUtcOffset(dayjs.tz.guess())
    },
    floorTimezoneMessage() {
      return (
        `This floor is in ${this.floorTimezoneCity} time (${this.floorTimezoneUtcOffset}) - ` +
        `different from your local timezone (${this.localTimezoneUtcOffset}).`
      )
    },
    timezoneBannerVisible() {
      return !!this.user?.booking_enabled && this.floorTimezoneMismatch
    },
    // Changes whenever the banner appears, disappears or starts showing another floor's
    // timezone, which is exactly when the auto-collapse countdown has to start over.
    timezoneBannerKey() {
      return this.timezoneBannerVisible ? this.floorTimezone : null
    },
    computedMarkers() {
      const points = this.availabilityPointsLoaded ? this.availabilityPoints : this.computedPoints
      return points.map((v) => ({
        id: v.id,
        latLng: serverCoordinateToLatLng(v.lat_lon, v.outdated),
        icon: this.computeMarkerIcon(v),
        draggable: v.id === this.activePointId && this.editMode,
        tooltip: formatTooltip({
          tooltip: v.title,
          hasSecond: this.departmentLabelEnabled,
          splitNamePart: this.printMode,
          fontSize: this.printMode ? this.printModeTitleSize : undefined
        }),
        tooltipOptions: {
          direction: this.markersScale ? 'bottom' : 'top'
        },
        permanentTooltip: this.printMode || this.displayTooltips
      }))
    },
    computedAreas() {
      if (!this.user.area_enabled) {
        return []
      }
      return this.points
        .filter((v) => v.icon_id === POINT_ICONS.AREA)
        .map((v) => ({
          id: v.id,
          coordinates: v.coordinates,
          name: this.getAreaTitle(v.title),
          color: v.color,
          editable: v.id === this.activePointId && this.editMode
        }))
    },
    markersScale() {
      if (this.printMode && this.markersHidden) return 0
      if (this.lastZoom === null || this.printMode) return 1
      if (!this.generalSettings.general_map_points_zoom_scaling) return 1
      const factor = this.generalSettings.general_map_points_zoom_scaling_factor || 1
      let scale
      if (this.lastZoom <= 17) scale = 0.4
      else if (this.lastZoom <= 18) scale = 0.5
      else if (this.lastZoom <= 20) scale = 0.75
      else if (this.lastZoom <= 21) scale = 1
      else scale = 2
      return scale * factor
    }
  },
  methods: {
    ...mapMutations({
      rootUpdate_: ROOT_UPDATE
    }),
    ...mapMutations('map', {
      mapUpdate_: MAP_UPDATE
    }),
    ...mapMutations('point', {
      pointCreate_: POINT_CREATE,
      pointUpdate_: POINT_UPDATE
    }),
    ...mapActions('map', ['resetZoom', 'setDefaultCenter', 'setCenter']),
    ...mapActions('point', ['pointsLoad', 'pointsSearch']),
    ...mapActions('desk', ['deskEventsSearch']),
    ...mapActions('floor', ['floorSave']),
    setPrintModeTitleSize(value) {
      clearTimeout(this.printModeTitleSizeTimeout)
      this.printModeTitleSizeTimeout = setTimeout(() => {
        let fontSize = parseInt(value)
        if (Number.isFinite(fontSize)) {
          if (fontSize < 5) {
            fontSize = 5
          } else if (fontSize > 20) {
            fontSize = 20
          }
          this.printModeTitleSize = fontSize
          // Dirty huck to set proper value in input,
          // or we get some wrong value if input wrong value twice
          const element = document.querySelector('#markers-label-size')
          if (element) {
            element.value = fontSize
          }
        }
      }, 20)
    },
    clearTimezoneBannerCollapseTimeout() {
      clearTimeout(this.timezoneBannerCollapseTimeout)
      this.timezoneBannerCollapseTimeout = null
    },
    scheduleTimezoneBannerCollapse() {
      this.clearTimezoneBannerCollapseTimeout()
      this.timezoneBannerCollapsed = false
      this.timezoneBannerCollapseTimeout = setTimeout(() => {
        this.timezoneBannerCollapsed = true
      }, TIMEZONE_BANNER_COLLAPSE_DELAY)
    },
    collapseTimezoneBanner() {
      this.clearTimezoneBannerCollapseTimeout()
      this.timezoneBannerCollapsed = true
    },
    expandTimezoneBanner() {
      // Opened by hand, so it stays open until it is closed by hand as well.
      this.clearTimezoneBannerCollapseTimeout()
      this.timezoneBannerCollapsed = false
    },
    getAreaTitle(name) {
      return this.printMode ? `<span style="font-size: ${this.printModeTitleSize}px">${name}</span>` : name
    },
    computeMarkerIcon(point) {
      const isActive = this.activePointId === point.id
      const pointIcon = this.getPointIcon(point.icon_id)
      if (!pointIcon) {
        console.error(`[Map]: pointIcon with id ${point.icon_id} not found`)
      }
      return { id: point.icon_id, ...getIconOptions(pointIcon, isActive, this.markersScale) }
    },
    toggleDepartmentLabel() {
      this.departmentLabelEnabled = !this.departmentLabelEnabled
    },
    toggleMarkersHide() {
      this.markersHidden = !this.markersHidden
    },
    saveCenterZoom() {
      const { lat, lng } = this.map.getBounds().getCenter()
      const center = `(${lat},${lng})`
      const zoom = this.map.getZoom()
      this.floorSave({ floor: { id: this.floorId, zoom, center } })
    },
    floorPointsLoad(floorId) {
      if (!floorId) return
      const config = {
        params: {
          filters: { floorId },
          expand: 'desk,printer,room,employee,pc'
        }
      }

      return this.pointsLoad(config)
    },
    closeAvailabilityFilter() {
      this.availabilityFilterEnabled = false
      this.availabilityPointsLoaded = false
      this.availabilityPoints = []
      this.availabilityConditions = {}
      this.floorPointsLoad(this.floorId)
    },
    async applyAvailabilityFilter({ from, to }) {
      this.availabilityPointsLoaded = false
      this.availabilityConditions = { from, to }
      const eventsSearchResponse = await this.deskEventsSearch({
        params: {
          filters: { since: from, until: to },
          pagerDisabled: true
        }
      })

      if (eventsSearchResponse?.status === 200 && eventsSearchResponse.data.items.length) {
        const deskPointsMap = {}
        this.points.forEach((point) => {
          if (point?.desk) deskPointsMap[point.desk] = point.id
        })

        const busyDeskPointsMap = {}
        eventsSearchResponse.data.items
          .filter((deskEvent) => {
            return deskEvent.end !== 'infinity' && deskEvent.desk_id && deskPointsMap[deskEvent.desk_id]
          })
          .forEach((deskEvent) => {
            const pointId = deskPointsMap[deskEvent.desk_id]
            busyDeskPointsMap[pointId] = true
          })

        const availabilityPoints = this.points
          .map((point) => {
            if (busyDeskPointsMap[point.id]) {
              point.icon_id = POINT_ICONS.DESK_BUSY
            } else if (+point.icon_id === POINT_ICONS.DESK_BUSY) {
              point.icon_id = POINT_ICONS.DESK
            }
            return point
          })
          .filter((v) => v.icon_id === POINT_ICONS.DESK && v.icon_id !== POINT_ICONS.AREA)

        this.availabilityPointsLoaded = true
        this.availabilityPoints = availabilityPoints
      }
    },
    changeFloor(floorId) {
      this.mapUpdate_({ floorId, activePointId: null })
    },
    handleZoom(zoom) {
      this.lastZoom = zoom
    },
    handleMapReady(map) {
      const printMode = Boolean(this.$route.query?.print)
      const editMode = Boolean(this.$route.query?.edit)
      this.mapUpdate_({ map, printMode, editMode })
      this.map.on('pm:create', this.handleFigureAdd)
      if (!this.mapCentringInProgress) {
        this.setCenterAndZoom()
      }
    },
    goToPoint(activePointId, editMode = false) {
      this.legendEnabled = false
      this.mapUpdate_({ activePointId, editMode })
    },
    goToLegend() {
      this.legendEnabled = true
    },
    goToPrintMode() {
      this.departmentLabelEnabled = false
      this.mapUpdate_({ printMode: true, activePointId: null, editMode: false })
    },
    goToNormalMode() {
      this.departmentLabelEnabled = true
      this.mapUpdate_({ printMode: false })
    },
    setDisplayTooltips() {
      const newState = !this.displayTooltips
      this.mapUpdate_({ displayTooltips: newState })
      this.departmentLabelEnabled = newState
    },
    print() {
      window.print()
    },
    // the map module outlives the screen: take everything the address describes from it,
    // or a mode left on by the last visit would be written into this one's address
    setStateFromRoute() {
      this.mapUpdate_(mapStateFromRoute(this.$route))
    },
    setCenterAndZoom(animate = true) {
      this.activePoint ? this.setActivePointCenterAndZoom(animate) : this.setFloorCenterAndZoom()
      this.mapCentringInProgress = false
    },
    setActivePointCenterAndZoom(animate) {
      const latLon = serverCoordinateToLatLng(this.activePoint.lat_lon, this.activePoint.outdated)
      const centerConfig = {
        center: latLon,
        animate,
        zoom: this.map.getZoom() < 21 ? 21 : this.map.getZoom()
      }

      // compensate mapPointPanel width
      const point = this.map.project(centerConfig.center, centerConfig.zoom)
      point.x += 185
      centerConfig.center = this.map.unproject(point, centerConfig.zoom)
      this.setCenter(centerConfig)
    },
    setFloorCenterAndZoom() {
      if (this.floor?.center && this.floor?.zoom) {
        const zoom = +this.floor.zoom
        const center = serverCoordinateToLatLng(this.floor.center, false)
        this.setCenter({ zoom, center })
      } else {
        this.setDefaultCenter()
      }
    },
    handleMarkerClick(id) {
      if (this.activePointId === id) return
      if (!isLocalId(id)) this.goToPoint(id, this.editMode)
    },
    handleMarkerMove({ id, lat, lng }) {
      this.pointUpdate_({ id, lat_lon: `(${lat},${lng})` })
    },
    handleAreaChange(payload) {
      const centroid = getCentroid(payload.coordinates)
      this.pointUpdate_({ ...payload, lat_lon: `(${centroid[0]},${centroid[1]})` })
    },

    // DRAWING
    handleStartDraw(iconId) {
      this.drawShapeId = iconId
      this.drawShapeType = determineShapeById(iconId)
      if (this.drawShapeType === SHAPES.Marker) {
        const pointIcon = this.getPointIcon(iconId)
        this.map.pm.enableDraw(this.drawShapeType, {
          markerStyle: {
            icon: new L.Icon(getIconOptions(pointIcon, true, this.markersScale))
          },
          snappable: false
        })
      } else if (this.drawShapeType === SHAPES.Polygon) {
        this.map.pm.enableDraw(this.drawShapeType)
      } else {
        console.error('[Map Draw] unsupported shape type to draw: ' + this.drawShapeType)
      }
    },
    disableShapeDrawing() {
      this.map.pm.disableDraw(this.drawShapeType)
      this.drawShapeId = null
      this.drawShapeType = null
    },
    /**
     * Handle draw new point from Leaflet
     * @param event
     */
    handleFigureAdd(event) {
      if (event.shape === SHAPES.Marker) {
        const latLon = event.marker.getLatLng()
        const newPoint = {
          id: Id(),
          lat_lon: `(${latLon.lat},${latLon.lng})`,
          floor_id: this.floorId,
          icon_id: this.drawShapeId,
          [POINT_TYPE_MAP[this.drawShapeId]]: Id()
        }
        this.pointCreate_(newPoint)
        this.goToPoint(newPoint.id, true)
      } else if (event.shape === SHAPES.Polygon) {
        const coordinates = event.marker.toGeoJSON()?.geometry?.coordinates[0]?.map(([x, y]) => [y, x])
        if (coordinates) {
          const centroid = getCentroid(coordinates)
          const newPoint = {
            id: Id(),
            lat_lon: `(${centroid[0]},${centroid[1]})`,
            coordinates,
            floor_id: this.floorId,
            icon_id: this.drawShapeId,
            [POINT_TYPE_MAP[this.drawShapeId]]: Id()
          }
          this.pointCreate_(newPoint)
          this.goToPoint(newPoint.id, true)
        } else {
          console.error('[Map Draw] cant get polygon coordinates!')
        }
      } else {
        console.error('[Map Draw] unsupported shape: ' + event.shape)
      }

      this.disableShapeDrawing()
      event.marker.remove()
    }
  },
  created() {
    const { pointId, bookFrom, bookTo } = this.$route.query || {}
    if (pointId && bookFrom && bookTo) {
      this.linkedBooking = { pointId: String(pointId), bookFrom, bookTo }
    }
  },
  async mounted() {
    const floorId = this.$route.params.floorId
    this.setStateFromRoute()
    this.mapUpdate_({ displayTooltips: !!this.generalSettings.general_map_tooltip_default_display })
    this.mapCentringInProgress = true
    this.floorPointsLoad(floorId).then(() => {
      this.setCenterAndZoom(false)
    })
    this.pointsRefreshInterval = setInterval(() => {
      this.floorPointsLoad(this.floorId)
    }, 2 * 60 * 1000)
  },
  beforeRouteEnter(to, from, next) {
    whenFloorsLoaded(() => {
      const floor = store.getters['floor/floors'].find((floor) => floor.id === +to.params.floorId)
      // the address being entered, not window.location: that is still the one being left
      floor && +floor.status === 1 ? next() : next(redirectLocation(to, notFoundLocation(to.path)))
    })
  },
  watch: {
    floorId(floorId) {
      this.mapCentringInProgress = true
      this.floorPointsLoad(floorId).then(() => {
        this.setCenterAndZoom()
        if (this.availabilityFilterEnabled) {
          this.applyAvailabilityFilter(this.availabilityConditions)
        }
      })
    },
    activePointId(v) {
      if (!this.mapCentringInProgress && v) {
        this.setCenterAndZoom()
      }
    },
    printMode: {
      immediate: true,
      handler(v) {
        this.rootUpdate_({ sidenavClass: v ? SIDENAV_CLASSES.disabled : '' })
      }
    },
    timezoneBannerKey: {
      immediate: true,
      handler(timezone) {
        if (timezone) {
          this.scheduleTimezoneBannerCollapse()
        } else {
          this.clearTimezoneBannerCollapseTimeout()
        }
      }
    }
  },
  destroyed() {
    this.map.off('pm:create', this.handleFigureAdd)
    clearInterval(this.pointsRefreshInterval)
    this.clearTimezoneBannerCollapseTimeout()
  }
}
</script>

<style lang="scss">
.close-icon {
  cursor: pointer;
  font-size: 22px;
  line-height: 12px;
  text-align: center;
  vertical-align: middle;
  font-weight: bold;
}
</style>

<style lang="scss">
.markers-label-size {
  display: flex;

  & label {
    white-space: nowrap;
  }

  &__input {
    width: 60px;
  }
}
.department-toggle {
  text-shadow: 1px 1px 0 #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 0 0 #fff;
  font-weight: bold;
}

.display-tooltips {
  padding-left: 27px;
}

.floor-container {
  position: relative;
}

#floor-map {
  width: auto;
  height: calc(100vh - 77px);
  background: #fff;

  &.floor-map_print {
    height: 100vh !important;
  }
}

.floor-actions {
  position: absolute;
  top: 1.2rem;
  left: 3.5rem;
  z-index: 997;

  @media print {
    display: none;
  }

  .floor-actions-row {
    display: flex;
    justify-items: left;
    align-items: center;
  }

  .floor-timezone-banner {
    margin-top: 0.5rem;
    padding: 0.35rem 0.7rem;
    background: white;
    border-radius: 5px;
    box-shadow: 0 0 0.25rem #0000003f;
    font-size: 12px;
    color: #666;
    display: inline-flex;
    align-items: center;

    &_collapsed {
      padding: 0.35rem 0.5rem;
    }

    &__icon-button {
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      font-size: 16px;
      line-height: 1;
      cursor: pointer;
    }

    &__close {
      margin-left: 0.6rem;

      &:hover {
        color: #333;
      }
    }

    &__expand {
      display: block;
      color: #f0ad4e;

      &:hover {
        color: #d9901f;
      }
    }
  }
}

.action-icon {
  margin-right: 8px;
  font-size: 18px;
}

.more-button {
  & > i {
    font-size: 22px;
    line-height: 12px !important;
  }
}

.more-menu {
  padding: 0;
  margin: 0;

  & > li {
    cursor: pointer;
  }
}

.print-mode-actions {
  position: absolute;
  top: 1rem;
  text-align: center;
  width: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-items: center;

  @media print {
    display: none;
  }
}

.dropdown-item {
  padding: 0.5rem 0.8rem;
}
</style>
